import { Component, EventEmitter, Input, Output, OnInit, Optional} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';

import { TranslateService } from '../../../../../services/translate.service';
import { DirectionService } from '../../../../../services/direction.service';
import { MaintainAccountService } from '../../../maintain-account/maintain-account.service';
import { ViewScopeService } from '../../../../../services/view-scope.service';

// PrimeNG Modules
import { InputTextModule } from 'primeng/inputtext';
import { InputTextarea } from 'primeng/inputtextarea';
import { ButtonModule } from 'primeng/button';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { RadioButtonModule } from 'primeng/radiobutton'; // For the default/modify choice
import { DynamicDialogRef, DynamicDialogConfig } from 'primeng/dynamicdialog';

// Interface for a Move Code entry
export interface MoveCode 
{
    id: string; // Unique ID for this move code entry
    recId: string;
    type: string; // Max 3 characters
    description: string;
    isActive: number;
    isApproved: number;
}

@Component({
    selector: 'app-move-codes',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        InputTextModule,
        InputTextarea,
        TagModule,
        ButtonModule,
        ToastModule,
        TableModule,
        RadioButtonModule // Include RadioButtonModule
    ],
    templateUrl: './move-codes.component.html',
    styleUrls: ['./move-codes.component.scss'],
    providers: [MessageService]
})
export class MoveCodesComponent implements OnInit {
    // Input: The list of move codes (if modifying)
    @Input() moveCodes: MoveCode[] = [];
    // Input: Boolean to indicate if default codes are used
    @Input() useDefaultMoveCodes: boolean = true; // Default to true


    @Input() customerRecId!: number;


    // Outputs to the parent component
    @Output() moveCodesChange = new EventEmitter<MoveCode[]>();
    @Output() useDefaultMoveCodesChange = new EventEmitter<boolean>();
    @Output() nextStep = new EventEmitter<void>();
    @Output() prevStep = new EventEmitter<void>();

    readonly TYPE_MAX_LENGTH = 3;
    readonly DESCRIPTION_MAX_LENGTH = 250;

    isDialogMode = false;
    isCreate = false;

    newEntry: MoveCode = {
        id: '',
        recId: '',
        type: '',
        description: '',
         isActive: 1 , isApproved: 1
    };

    // Model for the radio button choice (true for default, false for modify)
    selectedCodeOption: boolean = true;

    constructor(private messageService: MessageService,
                public translateServ :TranslateService,
                public dirServ : DirectionService,
                private viewScopeService: ViewScopeService,
                @Optional() public ref: DynamicDialogRef,
                @Optional() public config: DynamicDialogConfig,
                @Optional() private maintainAccountService: MaintainAccountService) {}

    ngOnInit(): void 
    {
        if (this.config && this.config.data) 
        {
            this.selectedCodeOption = false;
            this.isDialogMode = this.config.data.isDialog;
            this.customerRecId = this.config.data.customerRecId;
            this.isCreate = this.config.data.isCreate;

            if (!this.isCreate && this.config.data.nameEntry) 
            {
                // Edit mode: initialize with existing record(s)
                this.moveCodes = [this.config.data.nameEntry];
            } 
            else 
            {
                // Create mode: do NOT pre-push a row here.
                // The footer row with newEntry will handle input.
                this.moveCodes = [];
            }
        }
        else
        {
            this.isDialogMode = false;
            this.isCreate = true;
            
            // Ensure all existing items have a unique ID for tracking.
            this.moveCodes.forEach(item => 
            {
                if (!item.id) {
                    item.id = this.generateUniqueId();
                }
            });
            this.resetNewEntry();
        }
    }

    private generateUniqueId(): string {
        return 'move_' + Math.random().toString(36).substring(2, 9);
    }

    resetNewEntry(): void 
    {
        this.newEntry = {
            id: this.generateUniqueId(),
            recId: '',
            type: '',
            description: '',
            isActive: 1,      // default active
            isApproved: 1     // default approved
        };
    }

    

    /**
     * Handles the change event of the radio buttons for code options.
     * Converts the string value from the radio button to a boolean.
     * @param value The string value ('true' or 'false') from the radio button.
     */
    onCodeOptionChange(value: boolean): void 
    {
        this.selectedCodeOption = value;
        this.useDefaultMoveCodesChange.emit(value);

        if (value) {
            this.moveCodes = [];
            this.emitChanges();
        }
    }


    addEntry(): void {
        if (!this.newEntry.type.trim()) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: 'Type is required.',
                life: 3000
            });
            return;
        }
        if (this.newEntry.type.trim().length > this.TYPE_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Type cannot exceed ${this.TYPE_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }
        if (this.newEntry.description.length > this.DESCRIPTION_MAX_LENGTH) {
            this.messageService.add({
                severity: 'error',
                summary: 'Validation Error',
                detail: `Description exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                life: 3000
            });
            return;
        }

        // Check for duplicate types
        const isDuplicate = this.moveCodes.some(
            code => code.type.toLowerCase() === this.newEntry.type.trim().toLowerCase()
        );

        if (isDuplicate) {
            this.messageService.add({
                severity: 'warn',
                summary: 'Duplicate Entry',
                detail: 'A move code with this type already exists.',
                life: 3000
            });
            return;
        }

        // Add the new entry and reset the form
        this.moveCodes.push({ ...this.newEntry });
        this.emitChanges();
        
        if(this.isCreate)
        {
            this.resetNewEntry();    
        }
        
        this.messageService.add({
            severity: 'success',
            summary: 'Added',
            detail: 'New move code added.',
            life: 1500
        });
    }

    removeEntry(entryToRemove: MoveCode): void {
        this.moveCodes = this.moveCodes.filter(entry => entry.id !== entryToRemove.id);
        this.emitChanges();
        this.messageService.add({
            severity: 'info',
            summary: 'Removed',
            detail: 'Move code removed.',
            life: 1500
        });
    }

    emitChanges(): void {
        this.moveCodesChange.emit(this.moveCodes);
    }

    getRemainingChars(description: string): number {
        return this.DESCRIPTION_MAX_LENGTH - description.length;
    }

    validateAndProceed(): void {
        let isValid = true;

        if (!this.selectedCodeOption) 
        { 
            if (this.moveCodes.length === 0) {
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: 'At least one custom move code is required when "Modify Codes" is selected.',
                    life: 3000
                });
                isValid = false;
            } 
            else {
                for (const entry of this.moveCodes) {
                    if (!entry.type.trim()) {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Type is required for move code "${entry.id}".`,
                            life: 3000
                        });
                        break;
                    }
                    if ((entry.type?.length ?? 0) > this.TYPE_MAX_LENGTH) 
                        {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Type for move code "${entry.id}" cannot exceed ${this.TYPE_MAX_LENGTH} characters.`,
                            life: 3000
                        });
                        break;
                    }
                    if ((entry.description?.length ?? 0) > this.DESCRIPTION_MAX_LENGTH) 
                    {
                        isValid = false;
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Validation Error',
                            detail: `Description for move code "${entry.id}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                            life: 3000
                        });
                        break;
                    }
                }
            }
        }

        if (isValid) {
            this.nextStep.emit();
            this.messageService.clear();
        }
    }

    goToPreviousStep(): void {
        this.prevStep.emit();
    }

     applyRowChanges(entry: MoveCode): void {
            // Simply emit the changes so parent knows data is dirty
            this.emitChanges();
    
            this.messageService.add({
                severity: 'info',
                summary: 'Changes Applied',
                detail: `Changes applied for "${entry.type}". Remember to press Save to persist.`,
                life: 2500
            });
    }


    /**
     * This function is intended for the dialog's "save" button.
     * It validates the form and then calls the API to save/update the data.
     * It uses forkJoin to wait for all API calls to complete before closing the dialog.
     */
    saveAndSubmit(): void 
    {
        // First, run validation
        let isValid = true;

        for (const entry of this.moveCodes) {
            if (!entry.type.trim()) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Name for entry "${entry.id}" is required.`,
                    life: 3000
                });
                break;
            }
            if (entry.description.length > this.DESCRIPTION_MAX_LENGTH) {
                isValid = false;
                this.messageService.add({
                    severity: 'error',
                    summary: 'Validation Error',
                    detail: `Description for "${entry.type}" exceeds ${this.DESCRIPTION_MAX_LENGTH} characters.`,
                    life: 3000
                });
                break;
            }
        }

        if (!isValid) return;

        // Only attempt API calls if the service is available
        if (this.maintainAccountService) {
            const requests = this.moveCodes.map(entry => {
                const basePayload: any = {
                    CodeMoveType: entry.type,
                    Descr: entry.description || '', 
                    IsActive: entry.isActive,
                    IsApproved: entry.isApproved,
                    UserRecID: this.viewScopeService.getCurrentOpertaingUserId()!
                };

                if (this.isCreate) {
                    // For create → use CustomerRecID
                    basePayload.CustomerRecID = this.customerRecId;
                }
                else 
                {
                    // For edit → use RecID (taken from the entry provided by tab screen)
                    basePayload.RecID = entry.recId;  
                }

                return basePayload;
            });


            console.log("Data we will save:", requests);

            // Call API for each record separately
            requests.forEach(record => {
                this.maintainAccountService.insertOrUpdateRecord(this.isCreate, this.constructor.name.substring(1),JSON.stringify(record)).subscribe(
                {
                    next: (response) => {
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Success',
                            detail: `Saved record".`,
                            life: 2000
                        });

                        // Close dialog only after the last record
                        if (this.ref && record === requests[requests.length - 1]) {
                            this.ref.close(response);
                        }
                    },
                    error: (error) => {
                        this.messageService.add({
                            severity: 'error',
                            summary: 'Error',
                            detail: `Failed to save record".`,
                            life: 3000
                        });
                    },
                    complete: () => 
                    {
                        // This block runs after all API calls are complete
                        this.messageService.add({
                            severity: 'success',
                            summary: 'Complete',
                            detail: 'All records processed.',
                            life: 2000
                        });
                        // Reload data from API after successful submission
                        
                        //this.loadDataFromApi();


                        // Close dialog
                        if (this.ref) 
                        {
                            this.ref.close(true);
                        }
                    }
                });
            });

        } else {
            this.messageService.add({
                severity: 'warn',
                summary: 'Warning',
                detail: 'This functionality is not available in standalone mode.',
                life: 3000
            });
        }
    }

    
    getSeverity(val: boolean): string 
    {
        return val ? 'success' : 'danger';
    }

    toggleIsActive(entry: any): void 
    {
        entry.isActive = entry.isActive === 1 ? 0 : 1;
    }

    toggleIsApproved(entry: any): void 
    {
        entry.isApproved = entry.isApproved === 1 ? 0 : 1;
    }


    closeDialog(shouldReload: boolean = false): void {
        if (this.ref) {
            this.ref.close(shouldReload);
        }
    }
}
